import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge, Flex, Icon, IconButton, Table, Thead, Tr, Th, Tbody, Td, Box, Link, Button, Text,
  Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton, ModalFooter, useDisclosure,
  Tabs, TabList, TabPanels, TabPanel, Tab,
  Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody,
  Stat, StatLabel, StatNumber, StatHelpText, HStack,
  Input, Select, Tooltip, useMediaQuery, Tag,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon, MinusIcon } from '@chakra-ui/icons';
import { MdCalendarToday, MdNotifications, MdDescription, MdAssignment, MdThumbUp, MdThumbUpOffAlt, MdRefresh, MdBookmark, MdDeleteOutline } from 'react-icons/md';
import { FaSort, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import dayjs from 'dayjs';
import type { CapExRequest, NotificationType, ReviewRequestItem, ReviewRequestType, LanguageCode, BookmarkProjectItem } from '../../contexts/types';
import { ApplicationPathName, FlowKb, getStatusNameByCode, getPurposeTypeNameByCode, PurposeType, ApproveStatus, EquipmentUsageType, getEquipmentUsageTypeNameByCode, ReviewPhasePresets } from '../../contexts/constants';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { setEmployeeInfo } from '../../store/employeeSlice';
import { fetchLoginUserInfo, toEmployeeInfoPayload } from '../../hooks/useEnsureEmployeeInfo';
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { hasFilterString, hasFilterDateRange, openInvestmentScreeningDetailInNewTab } from '../../utils/utils';
import { getMultilingualDisplayName, getDivisionHierarchyDisplay, getHeadquartersNameFromHierarchy, getDivisionNameFromHierarchy } from '../../utils/i18nDisplay';
import TruncatedCell from '../../components/TruncatedCell';
import DebouncedInput from '../../components/DebouncedInput';
import ParallelApprovalIcons from '../../components/ParallelApprovalIcons';
import ConfirmModal from '../../components/ConfirmModal';
import { supportedLngs } from '../../utils/i18n';
import { getBookmarks, removeBookmark } from '../../utils/bookmarkStorage';

/** Date → input[type=date]用文字列 */
const formatDateForInput = (d: Date | null): string => {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** input[type=date]文字列 → Date */
const parseDateFromInput = (s: string): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** ソート設定 */
type SortConfig = { key: string; ascending: boolean };

/** CapExRequestからソート対象の値を取得 */
const getSortValue = (item: CapExRequest, column: string): string => {
  switch (column) {
    case "projectName": return item.projectName || "";
    case "purpose": return getPurposeTypeNameByCode(item.purpose || "") || "";
    case "equipmentUsageType": return getEquipmentUsageTypeNameByCode(item.equipmentUsageType || "") || "";
    case "headquarters": return getHeadquartersNameFromHierarchy(item)
    case "division": return getDivisionNameFromHierarchy(item)
    case "registrant": return item.registrant || "";
    case "applicationDate": return item.applicationDate || "";
    case "approveStatus": return item.approveStatus || "";
    case "currentApproverInfo": return item.currentApproverInfo || "";
    default: return "";
  }
};

/** カラムリサイズハンドル（スプレッドシート方式：対象列のみ幅変更、他列は維持） */
const ResizeHandle: React.FC<{
  colIndex: number;
  setColWidths: React.Dispatch<React.SetStateAction<number[]>>;
}> = ({ colIndex, setColWidths }) => {
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.target as HTMLElement).closest('th');
    if (!th) return;
    const tr = th.closest('tr');
    if (!tr) return;

    // 全カラムの現在のpx幅を取得して固定化
    const ths = Array.from(tr.querySelectorAll('th'));
    const currentWidths = ths.map(t => t.offsetWidth);
    setColWidths(currentWidths);

    const startX = e.clientX;
    const startWidth = currentWidths[colIndex];

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      setColWidths(prev => {
        const next = [...prev];
        next[colIndex] = Math.max(50, startWidth + delta);
        return next;
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <Box
      position="absolute"
      right="-2px"
      top={0}
      bottom={0}
      width="5px"
      cursor="col-resize"
      onMouseDown={handleMouseDown}
      _hover={{ bg: "blue.300" }}
      zIndex={2}
    />
  );
};

/**
 * 共通画面（ホーム画面）
 * @returns
 */
const Home: React.FC = () => {
  const { t } = useTranslation();
  const { email, isAuthorized } = useAppSelector((state) => state.auth);
  const [approveRequests, setApproveRequests] = useState<CapExRequest[]>([]);
  const [standbyForInputs, setStandbyForInput] = useState<CapExRequest[]>([]);
  const [pendingInvestments, setPendingInvestment] = useState<CapExRequest[]>([]);
  const [reviewRequests, setReviewRequests] = useState<ReviewRequestItem[]>([]);
  const [bookmarkProjects, setBookmarkProjects] = useState<BookmarkProjectItem[]>([]);
  const [bookmarkToRemove, setBookmarkToRemove] = useState<BookmarkProjectItem | null>(null);
  const { isOpen: isOpenBookmarkRemoveConfirm, onOpen: onOpenBookmarkRemoveConfirm, onClose: onCloseBookmarkRemoveConfirm } = useDisclosure();
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<NotificationType | null>(null);
  const [seenNotificationIds, setSeenNotificationIds] = useState<string[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const { isOpen: isOpenNotificationModal, onOpen: onOpenNotificationModal, onClose: onCloseNotificationModal } = useDisclosure();
  const { isOpen: isOpenDrawer, onOpen: onOpenDrawer, onClose: onCloseDrawer } = useDisclosure();
  const WebAPI = useContext(WebAPIContext);
  const dispatch = useAppDispatch();
  /** SUPPORTED_LANGUAGES/supportedLngsに追従する動的言語リスト */
  const langs = Object.keys(supportedLngs) as LanguageCode[];

  // フィルター・ソート用state: 承認依頼テーブル
  const [filterApproveProjectName, setFilterApproveProjectName] = useState<string>("");
  const [filterApprovePurpose, setFilterApprovePurpose] = useState<string>("");
  const [filterApproveEquipmentUsageType, setFilterApproveEquipmentUsageType] = useState<string>("");
  const [filterApproveHeadquarters, setFilterApproveHeadquarters] = useState<string>("");
  const [filterApproveDivision, setFilterApproveDivision] = useState<string>("");
  const [filterApproveRegistrant, setFilterApproveRegistrant] = useState<string>("");
  const [filterApproveStartDate, setFilterApproveStartDate] = useState<Date | null>(null);
  const [filterApproveEndDate, setFilterApproveEndDate] = useState<Date | null>(null);
  const [filterApproveCurrentApprover, setFilterApproveCurrentApprover] = useState<string>("");
  const [sortApprove, setSortApprove] = useState<SortConfig>({ key: "applicationDate", ascending: false });

  // フィルター・ソート用state: データ入力待ちテーブル
  const [filterStandbyProjectName, setFilterStandbyProjectName] = useState<string>("");
  const [filterStandbyPurpose, setFilterStandbyPurpose] = useState<string>("");
  const [filterStandbyEquipmentUsageType, setFilterStandbyEquipmentUsageType] = useState<string>("");
  const [filterStandbyHeadquarters, setFilterStandbyHeadquarters] = useState<string>("");
  const [filterStandbyDivision, setFilterStandbyDivision] = useState<string>("");
  const [filterStandbyRegistrant, setFilterStandbyRegistrant] = useState<string>("");
  const [sortStandby, setSortStandby] = useState<SortConfig>({ key: "projectName", ascending: true });

  // フィルター・ソート用state: 投資申請中テーブル
  const [filterPendingProjectName, setFilterPendingProjectName] = useState<string>("");
  const [filterPendingPurpose, setFilterPendingPurpose] = useState<string>("");
  const [filterPendingEquipmentUsageType, setFilterPendingEquipmentUsageType] = useState<string>("");
  const [filterPendingHeadquarters, setFilterPendingHeadquarters] = useState<string>("");
  const [filterPendingDivision, setFilterPendingDivision] = useState<string>("");
  const [filterPendingRegistrant, setFilterPendingRegistrant] = useState<string>("");
  const [filterPendingStartDate, setFilterPendingStartDate] = useState<Date | null>(null);
  const [filterPendingEndDate, setFilterPendingEndDate] = useState<Date | null>(null);
  const [filterPendingApprovalStatus, setFilterPendingApprovalStatus] = useState<string>("");
  const [sortPending, setSortPending] = useState<SortConfig>({ key: "applicationDate", ascending: false });

  // フィルター・ソート用state: 振り返り依頼テーブル
  const [filterReviewProjectName, setFilterReviewProjectName] = useState<string>("");
  const [filterReviewPurpose, setFilterReviewPurpose] = useState<string>("");
  const [filterReviewEquipmentUsageType, setFilterReviewEquipmentUsageType] = useState<string>("");
  const [filterReviewHeadquarters, setFilterReviewHeadquarters] = useState<string>("");
  const [filterReviewDivision, setFilterReviewDivision] = useState<string>("");
  const [filterReviewRegistrant, setFilterReviewRegistrant] = useState<string>("");
  const [filterReviewStartDate, setFilterReviewStartDate] = useState<Date | null>(null);
  const [filterReviewEndDate, setFilterReviewEndDate] = useState<Date | null>(null);
  const [filterReviewPhase, setFilterReviewPhase] = useState<string>("");
  const [filterReviewRequestType, setFilterReviewRequestType] = useState<string>("");
  const [filterReviewApprover, setFilterReviewApprover] = useState<string>("");
  const [sortReview, setSortReview] = useState<SortConfig>({ key: "targetReviewDate", ascending: true });

  // フィルター・ソート用state: ブックマークテーブル
  const [filterBookmarkProjectName, setFilterBookmarkProjectName] = useState<string>("");
  const [filterBookmarkPurpose, setFilterBookmarkPurpose] = useState<string>("");
  const [filterBookmarkEquipmentUsageType, setFilterBookmarkEquipmentUsageType] = useState<string>("");
  const [filterBookmarkHeadquarters, setFilterBookmarkHeadquarters] = useState<string>("");
  const [filterBookmarkDivision, setFilterBookmarkDivision] = useState<string>("");
  const [filterBookmarkCustomerName, setFilterBookmarkCustomerName] = useState<string>("");
  const [filterBookmarkOpportunityName, setFilterBookmarkOpportunityName] = useState<string>("");
  const [filterBookmarkRegistrant, setFilterBookmarkRegistrant] = useState<string>("");
  const [sortBookmark, setSortBookmark] = useState<SortConfig>({ key: "projectName", ascending: true });

  // カラムリサイズ用state（空配列＝初期幅はCSSパーセント、リサイズ開始時にpx化）
  const [pendingColWidths, setPendingColWidths] = useState<number[]>([]);
  const [standbyColWidths, setStandbyColWidths] = useState<number[]>([]);
  const [approveColWidths, setApproveColWidths] = useState<number[]>([]);
  const [reviewColWidths, setReviewColWidths] = useState<number[]>([]);
  const [bookmarkColWidths, setBookmarkColWidths] = useState<number[]>([]);

  interface GetProjectListResponse {
    data: {
      getProjectList: {
        resultCount: number;
        projects: Project[];
      };
    };
  }

  interface GetReviewRequestListResponse {
    data: {
      getReviewRequestList: {
        resultCount: number;
        projects: ReviewRequestItem[];
      };
    };
  }

  interface Project {
    projectId: string
    projectName: string;
    registrantName: string;
    applicationDay: string;
    approvalStatus: string;
    divisionHierarchy: string;
    divisionHierarchyEn?: string | null;
    purpose: string;
    equipmentUsageType: string | null;
    currentApproverInfo: string;
    parallelApprovalStatus: string | null;
    crossCheckRequired: boolean;
    materialOfficerRequired: boolean;
  }

  /** 日付ソート関数 */
  const sortDate = (value1: string | null | undefined, value2: string | null | undefined, ascending: boolean) => {
    try {
      if (!value1 && !value2) return 0;
      if (!value1) return ascending ? 1 : -1;
      if (!value2) return ascending ? -1 : 1;
      const date1 = new Date(value1);
      const date2 = new Date(value2);
      const isValidDate1 = !isNaN(date1.getTime());
      const isValidDate2 = !isNaN(date2.getTime());
      if (!isValidDate1 && !isValidDate2) return 0;
      if (!isValidDate1) return 1;
      if (!isValidDate2) return -1;
      if (ascending) {
        return date1.getTime() - date2.getTime();
      } else {
        return date2.getTime() - date1.getTime();
      }
    } catch (e) {
      return -1;
    }
  };

  /** 汎用ソート比較関数 */
  const compareBySort = (a: CapExRequest, b: CapExRequest, sort: SortConfig): number => {
    const valA = getSortValue(a, sort.key);
    const valB = getSortValue(b, sort.key);
    if (sort.key === "applicationDate") {
      return sortDate(valA, valB, sort.ascending);
    }
    if (!valA && !valB) return 0;
    if (!valA) return sort.ascending ? 1 : -1;
    if (!valB) return sort.ascending ? -1 : 1;
    return sort.ascending ? valA.localeCompare(valB, 'ja') : valB.localeCompare(valA, 'ja');
  };

  /** ソートボタン描画ヘルパー */
  const renderSortButton = (column: string, sort: SortConfig, setter: React.Dispatch<React.SetStateAction<SortConfig>>) => (
    <IconButton
      h="16px"
      onClick={() => setter(sort.key === column ? { key: column, ascending: !sort.ascending } : { key: column, ascending: true })}
      _hover={{ bg: "gray.100", cursor: "pointer" }}
      icon={sort.key === column ? (sort.ascending ? <FaSortAmountUp /> : <FaSortAmountDown />) : <FaSort />}
      aria-label={t('common.label.sort')}
    />
  );

  // フィルタリング＋ソート済みデータ: 承認依頼
  const filteredApproveRequests = useMemo(() => approveRequests
    .filter((item) => {
      const headquarters = getHeadquartersNameFromHierarchy(item)
      const division = getDivisionNameFromHierarchy(item)
      return (
        hasFilterString(item.projectName, filterApproveProjectName) &&
        hasFilterString(getPurposeTypeNameByCode(item.purpose || ""), filterApprovePurpose) &&
        hasFilterString(getEquipmentUsageTypeNameByCode(item.equipmentUsageType || ""), filterApproveEquipmentUsageType) &&
        hasFilterString(headquarters, filterApproveHeadquarters) &&
        hasFilterString(division, filterApproveDivision) &&
        hasFilterString(item.registrant, filterApproveRegistrant) &&
        hasFilterDateRange(item.applicationDate, filterApproveStartDate, filterApproveEndDate) &&
        hasFilterString(item.currentApproverInfo, filterApproveCurrentApprover)
      );
    })
    .sort((a, b) => compareBySort(a, b, sortApprove)),
    [approveRequests, filterApproveProjectName, filterApprovePurpose, filterApproveEquipmentUsageType, filterApproveHeadquarters, filterApproveDivision, filterApproveRegistrant, filterApproveStartDate, filterApproveEndDate, filterApproveCurrentApprover, sortApprove]);

  // フィルタリング＋ソート済みデータ: データ入力待ち
  const filteredStandbyForInputs = useMemo(() => standbyForInputs
    .filter((item) => {
      const headquarters = getHeadquartersNameFromHierarchy(item)
      const division = getDivisionNameFromHierarchy(item)
      return (
        hasFilterString(item.projectName, filterStandbyProjectName) &&
        hasFilterString(getPurposeTypeNameByCode(item.purpose || ""), filterStandbyPurpose) &&
        hasFilterString(getEquipmentUsageTypeNameByCode(item.equipmentUsageType || ""), filterStandbyEquipmentUsageType) &&
        hasFilterString(headquarters, filterStandbyHeadquarters) &&
        hasFilterString(division, filterStandbyDivision) &&
        hasFilterString(item.registrant, filterStandbyRegistrant)
      );
    })
    .sort((a, b) => compareBySort(a, b, sortStandby)),
    [standbyForInputs, filterStandbyProjectName, filterStandbyPurpose, filterStandbyEquipmentUsageType, filterStandbyHeadquarters, filterStandbyDivision, filterStandbyRegistrant, sortStandby]);

  // フィルタリング＋ソート済みデータ: 投資申請中
  const filteredPendingInvestments = useMemo(() => pendingInvestments
    .filter((item) => {
      const headquarters = getHeadquartersNameFromHierarchy(item)
      const division = getDivisionNameFromHierarchy(item)
      return (
        hasFilterString(item.projectName, filterPendingProjectName) &&
        hasFilterString(getPurposeTypeNameByCode(item.purpose || ""), filterPendingPurpose) &&
        hasFilterString(getEquipmentUsageTypeNameByCode(item.equipmentUsageType || ""), filterPendingEquipmentUsageType) &&
        hasFilterString(headquarters, filterPendingHeadquarters) &&
        hasFilterString(division, filterPendingDivision) &&
        hasFilterString(item.registrant, filterPendingRegistrant) &&
        hasFilterDateRange(item.applicationDate, filterPendingStartDate, filterPendingEndDate) &&
        hasFilterString(getStatusNameByCode(item.approveStatus || ""), filterPendingApprovalStatus)
      );
    })
    .sort((a, b) => compareBySort(a, b, sortPending)),
    [pendingInvestments, filterPendingProjectName, filterPendingPurpose, filterPendingEquipmentUsageType, filterPendingHeadquarters, filterPendingDivision, filterPendingRegistrant, filterPendingStartDate, filterPendingEndDate, filterPendingApprovalStatus, sortPending]);

  // 対応種別を翻訳ラベルに変換（フィルタ/ソート/表示で共用）
  const getRequestTypeLabel = (requestType: string | null | undefined): string => {
    if (requestType === "approval") return t('features.Home.requestType.approval');
    if (requestType === "confirmation") return t('features.Home.requestType.confirmation');
    if (requestType === "review") return t('features.Home.requestType.review');
    return "";
  };

  // 振り返り依頼テーブル用のソート値取得
  const getReviewSortValue = (item: ReviewRequestItem, column: string): string => {
    switch (column) {
      case "projectName": return item.projectName || "";
      case "purpose": return getPurposeTypeNameByCode(item.purpose || "") || "";
      case "equipmentUsageType": return getEquipmentUsageTypeNameByCode(item.equipmentUsageType || "") || "";
      case "headquarters": return getHeadquartersNameFromHierarchy(item)
      case "division": return getDivisionNameFromHierarchy(item)
      case "registrant": return item.registrantName || "";
      case "targetReviewDate": return item.targetReviewDate || "";
      case "reviewPhase": return item.reviewPhase || "";
      case "requestType": return getRequestTypeLabel(item.requestType);
      case "currentApproverInfo": return item.currentApproverInfo || "";
      default: return "";
    }
  };

  // フィルタリング＋ソート済みデータ: 振り返り依頼
  const filteredReviewRequests = useMemo(() => reviewRequests
    .filter((item) => {
      const headquarters = getHeadquartersNameFromHierarchy(item)
      const division = getDivisionNameFromHierarchy(item)
      return (
        hasFilterString(item.projectName, filterReviewProjectName) &&
        hasFilterString(getPurposeTypeNameByCode(item.purpose || ""), filterReviewPurpose) &&
        hasFilterString(getEquipmentUsageTypeNameByCode(item.equipmentUsageType || ""), filterReviewEquipmentUsageType) &&
        hasFilterString(headquarters, filterReviewHeadquarters) &&
        hasFilterString(division, filterReviewDivision) &&
        hasFilterString(item.registrantName, filterReviewRegistrant) &&
        hasFilterDateRange(item.targetReviewDate, filterReviewStartDate, filterReviewEndDate) &&
        hasFilterString(item.reviewPhase, filterReviewPhase) &&
        hasFilterString(getRequestTypeLabel(item.requestType), filterReviewRequestType) &&
        hasFilterString(item.currentApproverInfo, filterReviewApprover)
      );
    })
    .sort((a, b) => {
      const valA = getReviewSortValue(a, sortReview.key);
      const valB = getReviewSortValue(b, sortReview.key);
      if (sortReview.key === "targetReviewDate") {
        return sortDate(valA, valB, sortReview.ascending);
      }
      if (!valA && !valB) return 0;
      if (!valA) return sortReview.ascending ? 1 : -1;
      if (!valB) return sortReview.ascending ? -1 : 1;
      return sortReview.ascending ? valA.localeCompare(valB, 'ja') : valB.localeCompare(valA, 'ja');
    }),
    [reviewRequests, filterReviewProjectName, filterReviewPurpose, filterReviewEquipmentUsageType, filterReviewHeadquarters, filterReviewDivision, filterReviewRegistrant, filterReviewStartDate, filterReviewEndDate, filterReviewPhase, filterReviewRequestType, filterReviewApprover, sortReview, t]);

  // ブックマークテーブル用のソート値取得
  const getBookmarkSortValue = (item: BookmarkProjectItem, column: string): string => {
    switch (column) {
      case "projectName": return item.projectName || "";
      case "purpose": return getPurposeTypeNameByCode(item.purpose || "") || "";
      case "equipmentUsageType": return getEquipmentUsageTypeNameByCode(item.equipmentUsageType || "") || "";
      case "divisionHierarchy": return item.divisionHierarchy || "";
      case "customerName": return item.customerName || "";
      case "opportunityName": return item.opportunityName || "";
      case "registrant": return item.registrantName || "";
      case "applicationDate": return item.applicationDay || "";
      case "approveStatus": return item.accessDenied
        ? t('features.Home.bookmark.accessDenied')
        : item.deleted
          ? t('features.Home.bookmark.deleted')
          : (getStatusNameByCode(item.approvalStatus || "") || "");
      default: return "";
    }
  };

  // フィルタリング＋ソート済みデータ: ブックマーク
  const filteredBookmarkProjects = useMemo(() => bookmarkProjects
    .filter((item) => (
      hasFilterString(item.projectName, filterBookmarkProjectName) &&
      hasFilterString(getPurposeTypeNameByCode(item.purpose || ""), filterBookmarkPurpose) &&
      hasFilterString(getEquipmentUsageTypeNameByCode(item.equipmentUsageType || ""), filterBookmarkEquipmentUsageType) &&
      hasFilterString(getHeadquartersNameFromHierarchy(item), filterBookmarkHeadquarters) &&
      hasFilterString(getDivisionNameFromHierarchy(item), filterBookmarkDivision) &&
      hasFilterString(item.customerName, filterBookmarkCustomerName) &&
      hasFilterString(item.opportunityName, filterBookmarkOpportunityName) &&
      hasFilterString(item.registrantName, filterBookmarkRegistrant)
    ))
    .sort((a, b) => {
      const valA = getBookmarkSortValue(a, sortBookmark.key);
      const valB = getBookmarkSortValue(b, sortBookmark.key);
      if (sortBookmark.key === "applicationDate") {
        return sortDate(valA, valB, sortBookmark.ascending);
      }
      if (!valA && !valB) return 0;
      if (!valA) return sortBookmark.ascending ? 1 : -1;
      if (!valB) return sortBookmark.ascending ? -1 : 1;
      return sortBookmark.ascending ? valA.localeCompare(valB, 'ja') : valB.localeCompare(valA, 'ja');
    }),
    [bookmarkProjects, filterBookmarkProjectName, filterBookmarkPurpose, filterBookmarkEquipmentUsageType, filterBookmarkHeadquarters, filterBookmarkDivision, filterBookmarkCustomerName, filterBookmarkOpportunityName, filterBookmarkRegistrant, sortBookmark, t]);

  // プロジェクト一覧取得API（RDS移行対応版）
  const getProjectList = async (
    employeeId: string,
    flowKb: string | null
  ): Promise<GetProjectListResponse["data"]["getProjectList"] | undefined> => {
    try {
      console.log("getProjectList:start");
      const result: GetProjectListResponse = await WebAPI.request({
        query: `
          query getProjectList($employeeId: String, $flowKb: String) {
            getProjectList(employeeId: $employeeId, flowKb: $flowKb) {
              resultCount
              projects {
                projectId
                projectName
                registrantName
                applicationDay
                approvalStatus
                divisionHierarchy
                divisionHierarchyEn
                purpose
                equipmentUsageType
                currentApproverInfo
                parallelApprovalStatus
                crossCheckRequired
                materialOfficerRequired
              }
            }
          }
        `,
        variables: { employeeId, flowKb }
      });
      console.log(`getProjectList result:`, result);
      return result?.data?.getProjectList;
    } catch (error) {
      console.error("Error in getProjectList:", error);
      return undefined;
    }
  };

  // 振り返り依頼一覧取得API
  const getReviewRequestList = async (
    employeeId: string
  ): Promise<GetReviewRequestListResponse["data"]["getReviewRequestList"] | undefined> => {
    try {
      console.log("getReviewRequestList:start");
      const result: GetReviewRequestListResponse = await WebAPI.request({
        query: `
          query getReviewRequestList($employeeId: String!) {
            getReviewRequestList(employeeId: $employeeId) {
              resultCount
              projects {
                projectId
                projectName
                registrantName
                divisionHierarchy
                divisionHierarchyEn
                purpose
                equipmentUsageType
                targetReviewDate
                reviewPhase
                daysOverdue
                reviewStatus
              }
            }
          }
        `,
        variables: { employeeId }
      });
      console.log(`getReviewRequestList result:`, result);
      return result?.data?.getReviewRequestList;
    } catch (error) {
      console.error("Error in getReviewRequestList:", error);
      return undefined;
    }
  };

  // ブックマーク案件一覧取得API
  interface GetBookmarkProjectListResponse {
    data: {
      getBookmarkProjectList: BookmarkProjectItem[];
    };
  }

  /**
   * ブックマークプロジェクト情報取得
   * @param keys ブックマーク情報（projectIdとreviewDateのリスト）
   * @returns ブックマークプロジェクト情報
   */
  const getBookmarkProjectList = async (
    keys: { projectId: string; reviewDate: string | null }[]
  ): Promise<BookmarkProjectItem[] | undefined> => {
    try {
      console.log("getBookmarkProjectList:start");

      const result: GetBookmarkProjectListResponse = await WebAPI.request({
        query: `
          query getBookmarkProjectList($input: [BookmarkProjectKeyInput!]!) {
            getBookmarkProjectList(input: $input) {
              projectId
              reviewDate
              projectName
              purpose
              equipmentUsageType
              divisionHierarchy
              customerName
              opportunityName
              registrantName
              applicationDay
              approvalStatus
              deleted
              accessDenied
            }
          }
        `,
        variables: { input: keys }
      });
      console.log(`getBookmarkProjectList result:`, result);
      return result?.data?.getBookmarkProjectList;
    } catch (error) {
      console.error("Error in getBookmarkProjectList:", error);
      return undefined;
    }
  };

  /** ブックマーク一覧をローカルストレージ+APIから再取得する */
  const fetchBookmarkProjects = async () => {
    const keys = await getBookmarks();
    if (keys.length === 0) {
      setBookmarkProjects([]);
      return;
    }
    const result = await getBookmarkProjectList(keys);
    setBookmarkProjects(result || []);
  };

  // 振り返りワークフロー依頼一覧取得API（flowKb="0": 入力・確認依頼 / "1": 承認依頼）
  interface GetReviewWorkflowRequestListResponse {
    data: {
      getReviewWorkflowRequestList: {
        resultCount: number;
        projects: Array<{
          projectId: string;
          projectName: string;
          registrantName: string;
          divisionHierarchy: string;
          divisionHierarchyEn?: string | null;
          purpose: string;
          equipmentUsageType?: string | null;
          targetReviewDate: string;
          reviewPhase: string;
          approvalStatus: string;
          currentApproverInfo: string;
          parallelApprovalStatus: string | null;
          crossCheckRequired: boolean;
          materialOfficerRequired: boolean;
        }>;
      };
    };
  }

  const getReviewWorkflowRequestList = async (
    employeeId: string,
    flowKb: string
  ): Promise<GetReviewWorkflowRequestListResponse["data"]["getReviewWorkflowRequestList"] | undefined> => {
    try {
      console.log(`getReviewWorkflowRequestList:start flowKb=${flowKb}`);
      const result: GetReviewWorkflowRequestListResponse = await WebAPI.request({
        query: `
          query getReviewWorkflowRequestList($employeeId: String!, $flowKb: String!) {
            getReviewWorkflowRequestList(employeeId: $employeeId, flowKb: $flowKb) {
              resultCount
              projects {
                projectId
                projectName
                registrantName
                divisionHierarchy
                divisionHierarchyEn
                purpose
                equipmentUsageType
                targetReviewDate
                reviewPhase
                approvalStatus
                currentApproverInfo
                parallelApprovalStatus
                crossCheckRequired
                materialOfficerRequired
              }
            }
          }
        `,
        variables: { employeeId, flowKb }
      });
      console.log(`getReviewWorkflowRequestList result (flowKb=${flowKb}):`, result);
      return result?.data?.getReviewWorkflowRequestList;
    } catch (error) {
      console.error("Error in getReviewWorkflowRequestList:", error);
      return undefined;
    }
  };

  // ホーム画面に遷移した時点で振り返りモードを解除する。
  // sessionStorageにreviewDateが残っていると、案件リンクから通常モードで開こうとしても
  // 振り返りモードで開いてしまうため、ホーム画面到達時に必ずクリアする。
  useEffect(() => {
    sessionStorage.removeItem("reviewDate");
  }, []);

  useEffect(() => {
    if (email && isAuthorized) {
      const fetchData = async () => {
        try {
          // 新API: 複数所属対応のログインユーザー情報取得（Home画面遷移のたびに毎回再取得、既存通り）
          const loginUserInfo = await fetchLoginUserInfo(WebAPI.request, email);
          const employeeId = loginUserInfo?.userId;
          if (!employeeId) {
            console.error("Employee ID is undefined");
            return;
          }

          // ログイン者情報を設定（複数所属対応）
          dispatch(setEmployeeInfo(toEmployeeInfoPayload(loginUserInfo)));

          // ブックマークキーをローカルストレージから取得（一覧取得APIの呼び出し要否判定に使用）
          const bookmarkKeys = await getBookmarks();

          // プロジェクト情報の取得を並列で実行
          const [
            approveProjects,
            confirmationProjects,
            pendingProjects,
            reviewProjects,
            reviewConfirmationProjects,
            reviewApprovalProjects,
            notificationResult,
            bookmarkProjectResult,
          ] = await Promise.all([
            getProjectList(employeeId, FlowKb.Approval),
            getProjectList(employeeId, FlowKb.Confirmation),
            getProjectList(employeeId, null),
            getReviewRequestList(employeeId),
            getReviewWorkflowRequestList(employeeId, FlowKb.Confirmation),
            getReviewWorkflowRequestList(employeeId, FlowKb.Approval),
            WebAPI.request({
              query: `
                query {
                  getNotifications(publishedOnly: true) {
                    notificationId
                    title { ${langs.join(' ')} }
                    body { ${langs.join(' ')} }
                    publishDate
                    createDate
                  }
                }
              `
            }).catch((err: any) => { console.error("Error fetching notifications:", err); return null; }),
            bookmarkKeys.length > 0 ? getBookmarkProjectList(bookmarkKeys) : Promise.resolve([]),
          ]);
          setBookmarkProjects(bookmarkProjectResult || []);
          // 承認依頼待ちプロジェクトを取得（プレ検討会承認者（Lambda環境変数：MANAGEMENT_DEPT_ACCOUNT）を含む）
          if (!approveProjects || !approveProjects.projects) {
            console.log("ApproveProjects list is empty or fetch failed");
            setApproveRequests([]);
          } else {
            setApproveRequests(
              Array.from({ length: approveProjects.resultCount }, (_, i) => ({
                projectId: approveProjects.projects[i].projectId,
                projectName: approveProjects.projects[i].projectName,
                divisionHierarchy: approveProjects.projects[i].divisionHierarchy,
                divisionHierarchyEn: approveProjects.projects[i].divisionHierarchyEn,
                purpose: approveProjects.projects[i].purpose,
                equipmentUsageType: approveProjects.projects[i].equipmentUsageType,
                registrant: approveProjects.projects[i].registrantName,
                applicationDate: approveProjects.projects[i].applicationDay,
                approveStatus: approveProjects.projects[i].approvalStatus,
                currentApproverInfo: approveProjects.projects[i].currentApproverInfo,
                parallelApprovalStatus: approveProjects.projects[i].parallelApprovalStatus,
                crossCheckRequired: approveProjects.projects[i].crossCheckRequired,
                materialOfficerRequired: approveProjects.projects[i].materialOfficerRequired,
              }))
            );
          }
          // データ入力待ちプロジェクトを取得
          if (!confirmationProjects || !confirmationProjects.projects) {
            console.log("ConfirmationProjects list is empty or fetch failed");
            setStandbyForInput([]);
          } else {
            setStandbyForInput(
              Array.from({ length: confirmationProjects.resultCount }, (_, i) => ({
                projectId: confirmationProjects.projects[i].projectId,
                projectName: confirmationProjects.projects[i].projectName,
                divisionHierarchy: confirmationProjects.projects[i].divisionHierarchy,
                divisionHierarchyEn: confirmationProjects.projects[i].divisionHierarchyEn,
                purpose: confirmationProjects.projects[i].purpose,
                equipmentUsageType: confirmationProjects.projects[i].equipmentUsageType,
                registrant: confirmationProjects.projects[i].registrantName,
                applicationDate: confirmationProjects.projects[i].applicationDay,
              }))
            );
          }
          // 投資申請中プロジェクトを取得
          if (!pendingProjects || !pendingProjects.projects) {
            console.log("PendingProjects list is empty or fetch failed");
            setPendingInvestment([]);
          } else {
            setPendingInvestment(
              Array.from({ length: pendingProjects.resultCount }, (_, i) => ({
                projectId: pendingProjects.projects[i].projectId,
                projectName: pendingProjects.projects[i].projectName,
                divisionHierarchy: pendingProjects.projects[i].divisionHierarchy,
                divisionHierarchyEn: pendingProjects.projects[i].divisionHierarchyEn,
                purpose: pendingProjects.projects[i].purpose,
                equipmentUsageType: pendingProjects.projects[i].equipmentUsageType,
                registrant: pendingProjects.projects[i].registrantName,
                applicationDate: pendingProjects.projects[i].applicationDay,
                approveStatus: pendingProjects.projects[i].approvalStatus,
                parallelApprovalStatus: pendingProjects.projects[i].parallelApprovalStatus,
                crossCheckRequired: pendingProjects.projects[i].crossCheckRequired,
                materialOfficerRequired: pendingProjects.projects[i].materialOfficerRequired,
              }))
            );
          }
          // 振り返り依頼プロジェクトを取得（3ソースを projectId+reviewDate でマージ）
          const mergedReviewMap = new Map<string, ReviewRequestItem>();
          // 対応種別の優先度（大きい方が優先）
          const priority: Record<ReviewRequestType, number> = { review: 1, confirmation: 2, approval: 3 };
          // daysOverdue 計算用（ブラウザローカルTZ基準）
          const calcDaysOverdue = (targetDate: string): number => {
            if (!targetDate) return 0;
            const today = dayjs().startOf('day');
            const target = dayjs(targetDate).startOf('day');
            return today.diff(target, 'day');
          };

          // ソース1: 既存の起案者向け振り返り依頼
          if (reviewProjects?.projects) {
            for (const p of reviewProjects.projects) {
              const key = `${p.projectId}#${p.targetReviewDate}`;
              mergedReviewMap.set(key, {
                projectId: p.projectId,
                projectName: p.projectName,
                registrantName: p.registrantName,
                divisionHierarchy: p.divisionHierarchy,
                divisionHierarchyEn: p.divisionHierarchyEn,
                purpose: p.purpose || "",
                equipmentUsageType: p.equipmentUsageType,
                targetReviewDate: p.targetReviewDate,
                reviewPhase: p.reviewPhase,
                daysOverdue: p.daysOverdue,
                reviewStatus: p.reviewStatus,
                requestType: "review",
              });
            }
          }

          // ソース2: 振り返り入力・確認依頼
          if (reviewConfirmationProjects?.projects) {
            for (const p of reviewConfirmationProjects.projects) {
              const key = `${p.projectId}#${p.targetReviewDate}`;
              const existing = mergedReviewMap.get(key);
              const newType: ReviewRequestType = "confirmation";
              if (!existing || priority[newType] > priority[existing.requestType || "review"]) {
                mergedReviewMap.set(key, {
                  projectId: p.projectId,
                  projectName: p.projectName,
                  registrantName: p.registrantName,
                  divisionHierarchy: p.divisionHierarchy,
                  divisionHierarchyEn: p.divisionHierarchyEn,
                  purpose: p.purpose || "",
                  equipmentUsageType: p.equipmentUsageType,
                  targetReviewDate: p.targetReviewDate,
                  reviewPhase: p.reviewPhase,
                  daysOverdue: calcDaysOverdue(p.targetReviewDate),
                  reviewStatus: "inProgress",
                  requestType: newType,
                  approvalStatus: p.approvalStatus,
                });
              }
            }
          }

          // ソース3: 振り返り承認依頼
          if (reviewApprovalProjects?.projects) {
            for (const p of reviewApprovalProjects.projects) {
              const key = `${p.projectId}#${p.targetReviewDate}`;
              const existing = mergedReviewMap.get(key);
              const newType: ReviewRequestType = "approval";
              if (!existing || priority[newType] > priority[existing.requestType || "review"]) {
                mergedReviewMap.set(key, {
                  projectId: p.projectId,
                  projectName: p.projectName,
                  registrantName: p.registrantName,
                  divisionHierarchy: p.divisionHierarchy,
                  divisionHierarchyEn: p.divisionHierarchyEn,
                  purpose: p.purpose || "",
                  equipmentUsageType: p.equipmentUsageType,
                  targetReviewDate: p.targetReviewDate,
                  reviewPhase: p.reviewPhase,
                  daysOverdue: calcDaysOverdue(p.targetReviewDate),
                  reviewStatus: "inProgress",
                  requestType: newType,
                  approvalStatus: p.approvalStatus,
                  currentApproverInfo: p.currentApproverInfo,
                  parallelApprovalStatus: p.parallelApprovalStatus,
                  crossCheckRequired: p.crossCheckRequired,
                  materialOfficerRequired: p.materialOfficerRequired,
                });
              }
            }
          }

          // 同じ projectId の中で最も古い targetReviewDate のものだけを残す
          const uniqueReviewByProject = new Map<string, ReviewRequestItem>();
          for (const item of mergedReviewMap.values()) {
            const existing = uniqueReviewByProject.get(item.projectId);
            if (
              !existing ||
              (item.targetReviewDate &&
                (!existing.targetReviewDate || item.targetReviewDate < existing.targetReviewDate))
            ) {
              uniqueReviewByProject.set(item.projectId, item);
            }
          }
          setReviewRequests(Array.from(uniqueReviewByProject.values()));
          // お知らせを取得（最新1か月）
          if (notificationResult?.data?.getNotifications) {
            const oneMonthAgo = new Date();
            oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
            const oneMonthAgoStr = oneMonthAgo.toISOString().slice(0, 10);
            const items = notificationResult.data.getNotifications.filter(
              (n: NotificationType) => (n.publishDate || "") >= oneMonthAgoStr
            );
            setNotifications(items);
            const stored = localStorage.getItem('ceas_notification_seen_ids');
            const seenIds: string[] = stored ? JSON.parse(stored) : [];
            // 表示対象のお知らせIDのみに絞り込み（不要な既読IDを除去）
            const activeIds = new Set(items.map((n: NotificationType) => n.notificationId));
            const cleanedSeenIds = seenIds.filter(id => activeIds.has(id));
            if (cleanedSeenIds.length !== seenIds.length) {
              localStorage.setItem('ceas_notification_seen_ids', JSON.stringify(cleanedSeenIds));
            }
            setSeenNotificationIds(cleanedSeenIds);
          }
        } catch (error) {
          console.error("Error in fetchData:", error);
        }
      };
      fetchData();
    }
  }, [email, isAuthorized]);

  /** お知らせがNEW（未読）かどうかを判定 */
  const isNewNotification = (notification: NotificationType): boolean => {
    return !seenNotificationIds.includes(notification.notificationId);
  };

  /** 未読お知らせ件数 */
  const unreadCount = notifications.filter(n => isNewNotification(n)).length;

  /** お知らせ詳細モーダルを閉じる際にNEWバッジを更新 */
  const handleCloseNotificationModal = () => {
    if (selectedNotification) {
      const updatedIds = [...seenNotificationIds.filter(id => id !== selectedNotification.notificationId), selectedNotification.notificationId];
      setSeenNotificationIds(updatedIds);
      localStorage.setItem('ceas_notification_seen_ids', JSON.stringify(updatedIds));
    }
    onCloseNotificationModal();
  };

  /** サマリーカードの共通スタイル */
  const cardStyle = (index: number) => ({
    flex: 1,
    p: 4,
    bg: tabIndex === index ? "blue.50" : "white",
    borderRadius: "lg",
    boxShadow: "sm",
    border: "1px solid",
    borderColor: tabIndex === index ? "blue.300" : "gray.200",
    cursor: "pointer" as const,
    transition: "all 0.2s",
    _hover: { boxShadow: "md", borderColor: "blue.300" },
  });

  return (
    <Box width="100%" height="100%" display="flex" flexDirection="column" gap={4} p={2}>
      {/* サマリーカード + お知らせベルアイコン */}
      <Flex gap={4} align="center">
        <Box {...cardStyle(0)} onClick={() => setTabIndex(0)}>
          <Stat>
            <StatLabel fontSize="xs" color="gray.500"><Icon as={MdDescription} boxSize={3.5} mr={1} verticalAlign="text-bottom" />{t('features.Home.summaryCard.myRequest.title')}</StatLabel>
            <StatNumber fontSize="2xl" color={tabIndex === 0 ? "blue.600" : "gray.700"}>{filteredPendingInvestments.length}<Text as="span" fontSize="sm" fontWeight="normal" ml={1}>{t('common.label.count')}</Text></StatNumber>
            <StatHelpText fontSize="xs" color="gray.400" mb={0}>{t('features.Home.summaryCard.myRequest.description')}</StatHelpText>
          </Stat>
        </Box>
        <Box {...cardStyle(1)} onClick={() => setTabIndex(1)}>
          <Stat>
            <StatLabel fontSize="xs" color="gray.500"><Icon as={MdAssignment} boxSize={3.5} mr={1} verticalAlign="text-bottom" />{t('features.Home.summaryCard.inputRequest.title')}</StatLabel>
            <StatNumber fontSize="2xl" color={tabIndex === 1 ? "blue.600" : "gray.700"}>{filteredStandbyForInputs.length}<Text as="span" fontSize="sm" fontWeight="normal" ml={1}>{t('common.label.count')}</Text></StatNumber>
            <StatHelpText fontSize="xs" color="gray.400" mb={0}>{t('features.Home.summaryCard.inputRequest.description')}</StatHelpText>
          </Stat>
        </Box>
        <Box {...cardStyle(2)} onClick={() => setTabIndex(2)}>
          <Stat>
            <StatLabel fontSize="xs" color="gray.500"><Icon as={MdThumbUp} boxSize={3.5} mr={1} verticalAlign="text-bottom" />{t('features.Home.summaryCard.approveRequest.title')}</StatLabel>
            <StatNumber fontSize="2xl" color={tabIndex === 2 ? "blue.600" : "gray.700"}>{filteredApproveRequests.length}<Text as="span" fontSize="sm" fontWeight="normal" ml={1}>{t('common.label.count')}</Text></StatNumber>
            <StatHelpText fontSize="xs" color="gray.400" mb={0}>{t('features.Home.summaryCard.approveRequest.description')}</StatHelpText>
          </Stat>
        </Box>
        <Box {...cardStyle(3)} onClick={() => setTabIndex(3)}>
          <Stat>
            <StatLabel fontSize="xs" color="gray.500"><Icon as={MdRefresh} boxSize={3.5} mr={1} verticalAlign="text-bottom" />{t('features.Home.summaryCard.reviewRequest.title')}</StatLabel>
            <StatNumber fontSize="2xl" color={tabIndex === 3 ? "blue.600" : "gray.700"}>{filteredReviewRequests.length}<Text as="span" fontSize="sm" fontWeight="normal" ml={1}>{t('common.label.count')}</Text></StatNumber>
            <StatHelpText fontSize="xs" color="gray.400" mb={0}>{t('features.Home.summaryCard.reviewRequest.description')}</StatHelpText>
          </Stat>
        </Box>
        <Box {...cardStyle(4)} onClick={() => setTabIndex(4)}>
          <Stat>
            <StatLabel fontSize="xs" color="gray.500"><Icon as={MdBookmark} boxSize={3.5} mr={1} verticalAlign="text-bottom" />{t('features.Home.summaryCard.bookmark.title')}</StatLabel>
            <StatNumber fontSize="2xl" color={tabIndex === 4 ? "blue.600" : "gray.700"}>{filteredBookmarkProjects.length}<Text as="span" fontSize="sm" fontWeight="normal" ml={1}>{t('common.label.count')}</Text></StatNumber>
            <StatHelpText fontSize="xs" color="gray.400" mb={0}>{t('features.Home.summaryCard.bookmark.description')}</StatHelpText>
          </Stat>
        </Box>
        <Box position="relative">
          <IconButton
            aria-label={t('features.Home.ariaLabel.notification')}
            icon={<Icon as={MdNotifications} boxSize={6} />}
            variant="ghost"
            size="lg"
            onClick={onOpenDrawer}
          />
          {unreadCount > 0 && (
            <Badge
              colorScheme="red"
              borderRadius="full"
              position="absolute"
              top="0"
              right="0"
              fontSize="xs"
              minW="18px"
              textAlign="center"
            >
              {unreadCount}
            </Badge>
          )}
        </Box>
      </Flex>

      {/* タブ + テーブル */}
      <Box flex={1} bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden" display="flex" flexDirection="column">
        <Tabs index={tabIndex} onChange={setTabIndex} variant="line" colorScheme="blue" display="flex" flexDirection="column" flex={1} overflow="hidden">
          <TabList borderBottom="1px solid" borderColor="gray.200" px={4}>
            <Tab fontWeight="semibold" fontSize="sm"><Icon as={MdDescription} boxSize={3.5} mr={1} />{t('features.Home.summaryCard.myRequest.title')}</Tab>
            <Tab fontWeight="semibold" fontSize="sm"><Icon as={MdAssignment} boxSize={3.5} mr={1} />{t('features.Home.summaryCard.inputRequest.title')}</Tab>
            <Tab fontWeight="semibold" fontSize="sm"><Icon as={MdThumbUp} boxSize={3.5} mr={1} />{t('features.Home.summaryCard.approveRequest.title')}</Tab>
            <Tab fontWeight="semibold" fontSize="sm"><Icon as={MdRefresh} boxSize={3.5} mr={1} />{t('features.Home.summaryCard.reviewRequest.title')}</Tab>
            <Tab fontWeight="semibold" fontSize="sm"><Icon as={MdBookmark} boxSize={3.5} mr={1} />{t('features.Home.summaryCard.bookmark.title')}</Tab>
          </TabList>

          <TabPanels flex={1} overflow="hidden">
            {/* 投資申請中テーブル */}
            <TabPanel p={0} h="100%" overflow="auto">
              <Table border="1px solid" borderColor="gray.200" variant="simple" size="sm"
                sx={{ tableLayout: "fixed" }}
                width={pendingColWidths.length > 0 ? `${pendingColWidths.reduce((a, b) => a + b, 0)}px` : "100%"}
              >
                <Thead position="sticky" top={0} zIndex={1} sx={{ 'th input, th select': { ml: '-2px' } }}>
                  <Tr bgColor="gray.50">
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={pendingColWidths[0] ? `${pendingColWidths[0]}px` : "22%"} position="relative">
                      {t('features.Home.tableHeader.projectName')}{renderSortButton("projectName", sortPending, setSortPending)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterPendingProjectName} onChange={setFilterPendingProjectName} />
                      <ResizeHandle colIndex={0} setColWidths={setPendingColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={pendingColWidths[1] ? `${pendingColWidths[1]}px` : "10%"} position="relative">
                      {t('features.Home.tableHeader.purpose')}{renderSortButton("purpose", sortPending, setSortPending)}
                      <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterPendingPurpose} onChange={(e) => setFilterPendingPurpose(e.target.value)}>
                        <option value="">{t('common.label.all')}</option>
                        {Object.values(PurposeType).map(({ code, name }) => <option key={code} value={name}>{getPurposeTypeNameByCode(code)}</option>)}
                      </Select>
                      <ResizeHandle colIndex={1} setColWidths={setPendingColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={pendingColWidths[2] ? `${pendingColWidths[2]}px` : "9%"} position="relative">
                      <Box as="span" whiteSpace="nowrap">{t('features.Home.tableHeader.equipmentUsageType')}{renderSortButton("equipmentUsageType", sortPending, setSortPending)}</Box>
                      <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterPendingEquipmentUsageType} onChange={(e) => setFilterPendingEquipmentUsageType(e.target.value)}>
                        <option value="">{t('common.label.all')}</option>
                        {Object.values(EquipmentUsageType).map(({ code }) => <option key={code} value={getEquipmentUsageTypeNameByCode(code)}>{getEquipmentUsageTypeNameByCode(code)}</option>)}
                      </Select>
                      <ResizeHandle colIndex={2} setColWidths={setPendingColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={pendingColWidths[3] ? `${pendingColWidths[3]}px` : "12%"} position="relative">
                      {t('features.Home.tableHeader.headquarters')}{renderSortButton("headquarters", sortPending, setSortPending)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterPendingHeadquarters} onChange={setFilterPendingHeadquarters} />
                      <ResizeHandle colIndex={3} setColWidths={setPendingColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={pendingColWidths[4] ? `${pendingColWidths[4]}px` : "12%"} position="relative">
                      {t('features.Home.tableHeader.division')}{renderSortButton("division", sortPending, setSortPending)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterPendingDivision} onChange={setFilterPendingDivision} />
                      <ResizeHandle colIndex={4} setColWidths={setPendingColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={pendingColWidths[5] ? `${pendingColWidths[5]}px` : "12%"} position="relative">
                      {t('features.Home.tableHeader.registrant')}{renderSortButton("registrant", sortPending, setSortPending)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterPendingRegistrant} onChange={setFilterPendingRegistrant} />
                      <ResizeHandle colIndex={5} setColWidths={setPendingColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={pendingColWidths[6] ? `${pendingColWidths[6]}px` : "16%"} position="relative">
                      {t('features.Home.tableHeader.applicationDate')}{renderSortButton("applicationDate", sortPending, setSortPending)}
                      <Flex mt={1} align="center" gap={1}>
                        <Input type="date" size="xs" bg="white" fontWeight="normal" flex={1} value={formatDateForInput(filterPendingStartDate)} onChange={(e) => setFilterPendingStartDate(parseDateFromInput(e.target.value))} />
                        <Text fontSize="xs" fontWeight="normal" color="gray.500">〜</Text>
                        <Input type="date" size="xs" bg="white" fontWeight="normal" flex={1} value={formatDateForInput(filterPendingEndDate)} onChange={(e) => setFilterPendingEndDate(parseDateFromInput(e.target.value))} />
                      </Flex>
                      <ResizeHandle colIndex={6} setColWidths={setPendingColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={pendingColWidths[7] ? `${pendingColWidths[7]}px` : "16%"} position="relative">
                      {t('features.Home.tableHeader.approveStatus')}{renderSortButton("approveStatus", sortPending, setSortPending)}
                      <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterPendingApprovalStatus} onChange={(e) => setFilterPendingApprovalStatus(e.target.value)}>
                        <option value="">{t('common.label.all')}</option>
                        {Object.values(ApproveStatus).map(({ code, name }) => <option key={code} value={name}>{getStatusNameByCode(code)}</option>)}
                      </Select>
                      <ResizeHandle colIndex={7} setColWidths={setPendingColWidths} />
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredPendingInvestments.map(pendingInvestment => (
                    <Tr key={pendingInvestment.projectId} _hover={{ bg: "gray.50" }}>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={pendingInvestment.projectName || ""}>
                          <Link
                            as={RouterLink}
                            to={ApplicationPathName.InvestmentScreeningDetail.Url}
                            state={{ projectId: pendingInvestment.projectId }}
                            textColor={'blue.500'}
                            textDecoration={'underline'}
                            onClick={(e) => openInvestmentScreeningDetailInNewTab(e, pendingInvestment.projectId || "")}
                            onAuxClick={(e) => openInvestmentScreeningDetailInNewTab(e, pendingInvestment.projectId || "")}
                          >
                            {pendingInvestment.projectName}
                          </Link>
                        </TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getPurposeTypeNameByCode(pendingInvestment.purpose || "") || ""}>{getPurposeTypeNameByCode(pendingInvestment.purpose || "")}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getEquipmentUsageTypeNameByCode(pendingInvestment.equipmentUsageType || "") || ""}>{getEquipmentUsageTypeNameByCode(pendingInvestment.equipmentUsageType || "")}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getHeadquartersNameFromHierarchy(pendingInvestment)}>{getHeadquartersNameFromHierarchy(pendingInvestment)}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getDivisionNameFromHierarchy(pendingInvestment)}>{getDivisionNameFromHierarchy(pendingInvestment)}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={pendingInvestment.registrant || ""}>{pendingInvestment.registrant}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={pendingInvestment.applicationDate || ""}>{pendingInvestment.applicationDate}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        {pendingInvestment.parallelApprovalStatus ? (
                          <ParallelApprovalIcons
                            approvalStatus={pendingInvestment.approveStatus || ""}
                            parallelApprovalStatus={pendingInvestment.parallelApprovalStatus}
                            crossCheckRequired={pendingInvestment.crossCheckRequired}
                            materialOfficerRequired={pendingInvestment.materialOfficerRequired}
                          />
                        ) : (
                          <TruncatedCell label={getStatusNameByCode(pendingInvestment.approveStatus || "") || ""}>{getStatusNameByCode(pendingInvestment.approveStatus || "")}</TruncatedCell>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TabPanel>

            {/* データ入力待ちテーブル */}
            <TabPanel p={0} h="100%" overflow="auto">
              <Table border="1px solid" borderColor="gray.200" variant="simple" size="sm"
                sx={{ tableLayout: "fixed" }}
                width={standbyColWidths.length > 0 ? `${standbyColWidths.reduce((a, b) => a + b, 0)}px` : "68%"}
              >
                <Thead position="sticky" top={0} zIndex={1} sx={{ 'th input, th select': { ml: '-2px' } }}>
                  <Tr bgColor="gray.50">
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={standbyColWidths[0] ? `${standbyColWidths[0]}px` : "22%"} position="relative">
                      {t('features.Home.tableHeader.projectName')}{renderSortButton("projectName", sortStandby, setSortStandby)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterStandbyProjectName} onChange={setFilterStandbyProjectName} />
                      <ResizeHandle colIndex={0} setColWidths={setStandbyColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={standbyColWidths[1] ? `${standbyColWidths[1]}px` : "10%"} position="relative">
                      {t('features.Home.tableHeader.purpose')}{renderSortButton("purpose", sortStandby, setSortStandby)}
                      <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterStandbyPurpose} onChange={(e) => setFilterStandbyPurpose(e.target.value)}>
                        <option value="">{t('common.label.all')}</option>
                        {Object.values(PurposeType).map(({ code, name }) => <option key={code} value={name}>{getPurposeTypeNameByCode(code)}</option>)}
                      </Select>
                      <ResizeHandle colIndex={1} setColWidths={setStandbyColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={standbyColWidths[2] ? `${standbyColWidths[2]}px` : "9%"} position="relative">
                      <Box as="span" whiteSpace="nowrap">{t('features.Home.tableHeader.equipmentUsageType')}{renderSortButton("equipmentUsageType", sortStandby, setSortStandby)}</Box>
                      <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterStandbyEquipmentUsageType} onChange={(e) => setFilterStandbyEquipmentUsageType(e.target.value)}>
                        <option value="">{t('common.label.all')}</option>
                        {Object.values(EquipmentUsageType).map(({ code }) => <option key={code} value={getEquipmentUsageTypeNameByCode(code)}>{getEquipmentUsageTypeNameByCode(code)}</option>)}
                      </Select>
                      <ResizeHandle colIndex={2} setColWidths={setStandbyColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={standbyColWidths[3] ? `${standbyColWidths[3]}px` : "12%"} position="relative">
                      {t('features.Home.tableHeader.headquarters')}{renderSortButton("headquarters", sortStandby, setSortStandby)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterStandbyHeadquarters} onChange={setFilterStandbyHeadquarters} />
                      <ResizeHandle colIndex={3} setColWidths={setStandbyColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={standbyColWidths[4] ? `${standbyColWidths[4]}px` : "12%"} position="relative">
                      {t('features.Home.tableHeader.division')}{renderSortButton("division", sortStandby, setSortStandby)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterStandbyDivision} onChange={setFilterStandbyDivision} />
                      <ResizeHandle colIndex={4} setColWidths={setStandbyColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={standbyColWidths[5] ? `${standbyColWidths[5]}px` : "12%"} position="relative">
                      {t('features.Home.tableHeader.registrant')}{renderSortButton("registrant", sortStandby, setSortStandby)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterStandbyRegistrant} onChange={setFilterStandbyRegistrant} />
                      <ResizeHandle colIndex={5} setColWidths={setStandbyColWidths} />
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredStandbyForInputs.map(standbyForInput => (
                    <Tr key={standbyForInput.projectId} _hover={{ bg: "gray.50" }}>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={standbyForInput.projectName || ""}>
                          <Link
                            as={RouterLink}
                            to={ApplicationPathName.InvestmentScreeningDetail.Url}
                            state={{ projectId: standbyForInput.projectId }}
                            textColor={'blue.500'}
                            textDecoration={'underline'}
                            onClick={(e) => openInvestmentScreeningDetailInNewTab(e, standbyForInput.projectId || "")}
                            onAuxClick={(e) => openInvestmentScreeningDetailInNewTab(e, standbyForInput.projectId || "")}
                          >
                            {standbyForInput.projectName}
                          </Link>
                        </TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getPurposeTypeNameByCode(standbyForInput.purpose || "") || ""}>{getPurposeTypeNameByCode(standbyForInput.purpose || "")}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getEquipmentUsageTypeNameByCode(standbyForInput.equipmentUsageType || "") || ""}>{getEquipmentUsageTypeNameByCode(standbyForInput.equipmentUsageType || "")}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getHeadquartersNameFromHierarchy(standbyForInput)}>{getHeadquartersNameFromHierarchy(standbyForInput)}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getDivisionNameFromHierarchy(standbyForInput)}>{getDivisionNameFromHierarchy(standbyForInput)}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={standbyForInput.registrant || ""}>{standbyForInput.registrant}</TruncatedCell>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TabPanel>

            {/* 承認依頼テーブル */}
            <TabPanel p={0} h="100%" overflow="auto">
              <Table border="1px solid" borderColor="gray.200" variant="simple" size="sm"
                sx={{ tableLayout: "fixed" }}
                width={approveColWidths.length > 0 ? `${approveColWidths.reduce((a, b) => a + b, 0)}px` : "100%"}
              >
                <Thead position="sticky" top={0} zIndex={1} sx={{ 'th input, th select': { ml: '-2px' } }}>
                  <Tr bgColor="gray.50">
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={approveColWidths[0] ? `${approveColWidths[0]}px` : "22%"} position="relative">
                      {t('features.Home.tableHeader.projectName')}{renderSortButton("projectName", sortApprove, setSortApprove)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterApproveProjectName} onChange={setFilterApproveProjectName} />
                      <ResizeHandle colIndex={0} setColWidths={setApproveColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={approveColWidths[1] ? `${approveColWidths[1]}px` : "10%"} position="relative">
                      {t('features.Home.tableHeader.purpose')}{renderSortButton("purpose", sortApprove, setSortApprove)}
                      <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterApprovePurpose} onChange={(e) => setFilterApprovePurpose(e.target.value)}>
                        <option value="">{t('common.label.all')}</option>
                        {Object.values(PurposeType).map(({ code, name }) => <option key={code} value={name}>{getPurposeTypeNameByCode(code)}</option>)}
                      </Select>
                      <ResizeHandle colIndex={1} setColWidths={setApproveColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={approveColWidths[2] ? `${approveColWidths[2]}px` : "9%"} position="relative">
                      <Box as="span" whiteSpace="nowrap">{t('features.Home.tableHeader.equipmentUsageType')}{renderSortButton("equipmentUsageType", sortApprove, setSortApprove)}</Box>
                      <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterApproveEquipmentUsageType} onChange={(e) => setFilterApproveEquipmentUsageType(e.target.value)}>
                        <option value="">{t('common.label.all')}</option>
                        {Object.values(EquipmentUsageType).map(({ code }) => <option key={code} value={getEquipmentUsageTypeNameByCode(code)}>{getEquipmentUsageTypeNameByCode(code)}</option>)}
                      </Select>
                      <ResizeHandle colIndex={2} setColWidths={setApproveColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={approveColWidths[3] ? `${approveColWidths[3]}px` : "12%"} position="relative">
                      {t('features.Home.tableHeader.headquarters')}{renderSortButton("headquarters", sortApprove, setSortApprove)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterApproveHeadquarters} onChange={setFilterApproveHeadquarters} />
                      <ResizeHandle colIndex={3} setColWidths={setApproveColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={approveColWidths[4] ? `${approveColWidths[4]}px` : "12%"} position="relative">
                      {t('features.Home.tableHeader.division')}{renderSortButton("division", sortApprove, setSortApprove)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterApproveDivision} onChange={setFilterApproveDivision} />
                      <ResizeHandle colIndex={4} setColWidths={setApproveColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={approveColWidths[5] ? `${approveColWidths[5]}px` : "12%"} position="relative">
                      {t('features.Home.tableHeader.registrant')}{renderSortButton("registrant", sortApprove, setSortApprove)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterApproveRegistrant} onChange={setFilterApproveRegistrant} />
                      <ResizeHandle colIndex={5} setColWidths={setApproveColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={approveColWidths[6] ? `${approveColWidths[6]}px` : "16%"} position="relative">
                      {t('features.Home.tableHeader.applicationDate')}{renderSortButton("applicationDate", sortApprove, setSortApprove)}
                      <Flex mt={1} align="center" gap={1}>
                        <Input type="date" size="xs" bg="white" fontWeight="normal" flex={1} value={formatDateForInput(filterApproveStartDate)} onChange={(e) => setFilterApproveStartDate(parseDateFromInput(e.target.value))} />
                        <Text fontSize="xs" fontWeight="normal" color="gray.500">〜</Text>
                        <Input type="date" size="xs" bg="white" fontWeight="normal" flex={1} value={formatDateForInput(filterApproveEndDate)} onChange={(e) => setFilterApproveEndDate(parseDateFromInput(e.target.value))} />
                      </Flex>
                      <ResizeHandle colIndex={6} setColWidths={setApproveColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={approveColWidths[7] ? `${approveColWidths[7]}px` : "16%"} position="relative">
                      {t('features.Home.tableHeader.waitingApprover')}{renderSortButton("currentApproverInfo", sortApprove, setSortApprove)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterApproveCurrentApprover} onChange={setFilterApproveCurrentApprover} />
                      <ResizeHandle colIndex={7} setColWidths={setApproveColWidths} />
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredApproveRequests.map(approveRequest => (
                    <Tr key={approveRequest.projectId} _hover={{ bg: "gray.50" }}>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={approveRequest.projectName || ""}>
                          <Link
                            as={RouterLink}
                            to={ApplicationPathName.InvestmentScreeningDetail.Url}
                            state={{ projectId: approveRequest.projectId }}
                            textColor={'blue.500'}
                            textDecoration={'underline'}
                            onClick={(e) => openInvestmentScreeningDetailInNewTab(e, approveRequest.projectId || "")}
                            onAuxClick={(e) => openInvestmentScreeningDetailInNewTab(e, approveRequest.projectId || "")}
                          >
                            {approveRequest.projectName}
                          </Link>
                        </TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getPurposeTypeNameByCode(approveRequest.purpose || "") || ""}>{getPurposeTypeNameByCode(approveRequest.purpose || "")}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getEquipmentUsageTypeNameByCode(approveRequest.equipmentUsageType || "") || ""}>{getEquipmentUsageTypeNameByCode(approveRequest.equipmentUsageType || "")}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getHeadquartersNameFromHierarchy(approveRequest)}>{getHeadquartersNameFromHierarchy(approveRequest)}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getDivisionNameFromHierarchy(approveRequest)}>{getDivisionNameFromHierarchy(approveRequest)}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={approveRequest.registrant || ""}>{approveRequest.registrant}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={approveRequest.applicationDate || ""}>{approveRequest.applicationDate}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        {approveRequest.parallelApprovalStatus ? (
                          <ParallelApprovalIcons
                            approvalStatus={approveRequest.approveStatus || ""}
                            parallelApprovalStatus={approveRequest.parallelApprovalStatus}
                            crossCheckRequired={approveRequest.crossCheckRequired}
                            materialOfficerRequired={approveRequest.materialOfficerRequired}
                          />
                        ) : (
                          <TruncatedCell label={approveRequest.currentApproverInfo || ""}>{approveRequest.currentApproverInfo}</TruncatedCell>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TabPanel>

            {/* 振り返り依頼テーブル */}
            <TabPanel p={0} h="100%" overflow="auto">
              {/* maxWidth: 100%だと丸め誤差で横スクロールが発生するため99.9%で強制的に親幅内に収める */}
              <Table border="1px solid" borderColor="gray.200" variant="simple" size="sm"
                sx={{ tableLayout: "fixed" }}
                minWidth="2003px"
                maxWidth="99.9%"
                width={reviewColWidths.length > 0 ? `${reviewColWidths.reduce((a, b) => a + b, 0)}px` : "100%"}
              >
                <Thead position="sticky" top={0} zIndex={2} sx={{ 'th input, th select': { ml: '-2px' } }}>
                  <Tr bgColor="gray.50">
                    {/* 左端列（設備投資PROJECT名）は横スクロール時も固定表示 */}
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={reviewColWidths[0] ? `${reviewColWidths[0]}px` : "22%"} position="sticky" left={0} zIndex={3}>
                      {t('features.Home.tableHeader.projectName')}{renderSortButton("projectName", sortReview, setSortReview)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterReviewProjectName} onChange={setFilterReviewProjectName} />
                      <ResizeHandle colIndex={0} setColWidths={setReviewColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={reviewColWidths[1] ? `${reviewColWidths[1]}px` : "10%"} position="relative">
                      {t('features.Home.tableHeader.purpose')}{renderSortButton("purpose", sortReview, setSortReview)}
                      <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterReviewPurpose} onChange={(e) => setFilterReviewPurpose(e.target.value)}>
                        <option value="">{t('common.label.all')}</option>
                        {Object.values(PurposeType).map(({ code, name }) => <option key={code} value={name}>{getPurposeTypeNameByCode(code)}</option>)}
                      </Select>
                      <ResizeHandle colIndex={1} setColWidths={setReviewColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={reviewColWidths[2] ? `${reviewColWidths[2]}px` : "9%"} position="relative">
                      <Box as="span" whiteSpace="nowrap">{t('features.Home.tableHeader.equipmentUsageType')}{renderSortButton("equipmentUsageType", sortReview, setSortReview)}</Box>
                      <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterReviewEquipmentUsageType} onChange={(e) => setFilterReviewEquipmentUsageType(e.target.value)}>
                        <option value="">{t('common.label.all')}</option>
                        {Object.values(EquipmentUsageType).map(({ code }) => <option key={code} value={getEquipmentUsageTypeNameByCode(code)}>{getEquipmentUsageTypeNameByCode(code)}</option>)}
                      </Select>
                      <ResizeHandle colIndex={2} setColWidths={setReviewColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={reviewColWidths[3] ? `${reviewColWidths[3]}px` : "12%"} position="relative">
                      {t('features.Home.tableHeader.headquarters')}{renderSortButton("headquarters", sortReview, setSortReview)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterReviewHeadquarters} onChange={setFilterReviewHeadquarters} />
                      <ResizeHandle colIndex={3} setColWidths={setReviewColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={reviewColWidths[4] ? `${reviewColWidths[4]}px` : "12%"} position="relative">
                      {t('features.Home.tableHeader.division')}{renderSortButton("division", sortReview, setSortReview)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterReviewDivision} onChange={setFilterReviewDivision} />
                      <ResizeHandle colIndex={4} setColWidths={setReviewColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={reviewColWidths[5] ? `${reviewColWidths[5]}px` : "10%"} position="relative">
                      {t('features.Home.tableHeader.registrant')}{renderSortButton("registrant", sortReview, setSortReview)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterReviewRegistrant} onChange={setFilterReviewRegistrant} />
                      <ResizeHandle colIndex={5} setColWidths={setReviewColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={reviewColWidths[6] ? `${reviewColWidths[6]}px` : "14%"} position="relative">
                      {t('features.Home.tableHeader.reviewDate')}{renderSortButton("targetReviewDate", sortReview, setSortReview)}
                      <Flex mt={1} align="center" gap={1}>
                        <Input type="date" size="xs" bg="white" fontWeight="normal" flex={1} value={formatDateForInput(filterReviewStartDate)} onChange={(e) => setFilterReviewStartDate(parseDateFromInput(e.target.value))} />
                        <Text fontSize="xs" fontWeight="normal" color="gray.500">〜</Text>
                        <Input type="date" size="xs" bg="white" fontWeight="normal" flex={1} value={formatDateForInput(filterReviewEndDate)} onChange={(e) => setFilterReviewEndDate(parseDateFromInput(e.target.value))} />
                      </Flex>
                      <ResizeHandle colIndex={6} setColWidths={setReviewColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={reviewColWidths[7] ? `${reviewColWidths[7]}px` : "8%"} position="relative">
                      {t('features.Home.tableHeader.phase')}{renderSortButton("reviewPhase", sortReview, setSortReview)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterReviewPhase} onChange={setFilterReviewPhase} />
                      <ResizeHandle colIndex={7} setColWidths={setReviewColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={reviewColWidths[8] ? `${reviewColWidths[8]}px` : "14%"} position="relative">
                      {t('features.Home.tableHeader.requestType')}{renderSortButton("requestType", sortReview, setSortReview)}
                      <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterReviewRequestType} onChange={(e) => setFilterReviewRequestType(e.target.value)}>
                        <option value="">{t('common.label.all')}</option>
                        <option value={t('features.Home.requestType.review')}>{t('features.Home.requestType.review')}</option>
                        <option value={t('features.Home.requestType.confirmation')}>{t('features.Home.requestType.confirmation')}</option>
                        <option value={t('features.Home.requestType.approval')}>{t('features.Home.requestType.approval')}</option>
                      </Select>
                      <ResizeHandle colIndex={8} setColWidths={setReviewColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={reviewColWidths[9] ? `${reviewColWidths[9]}px` : "12%"} position="relative">
                      {t('features.Home.tableHeader.waitingApprover')}{renderSortButton("currentApproverInfo", sortReview, setSortReview)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterReviewApprover} onChange={setFilterReviewApprover} />
                      <ResizeHandle colIndex={9} setColWidths={setReviewColWidths} />
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredReviewRequests.map(reviewRequest => (
                    <Tr key={`${reviewRequest.projectId}#${reviewRequest.targetReviewDate}`} role="group" _hover={{ bg: "gray.50" }}>
                      {/* 左端列（設備投資PROJECT名）は横スクロール時も固定表示 */}
                      <Td borderRight="1px solid" borderColor="gray.200" position="sticky" left={0} zIndex={1} bg="white" _groupHover={{ bg: "gray.50" }}>
                        <TruncatedCell label={reviewRequest.projectName || ""}>
                          <Link
                            as={RouterLink}
                            to={ApplicationPathName.InvestmentScreeningDetail.Url}
                            state={{ projectId: reviewRequest.projectId, reviewDate: reviewRequest.targetReviewDate }}
                            textColor={'blue.500'}
                            textDecoration={'underline'}
                            onClick={(e) => openInvestmentScreeningDetailInNewTab(e, reviewRequest.projectId || "", reviewRequest.targetReviewDate)}
                            onAuxClick={(e) => openInvestmentScreeningDetailInNewTab(e, reviewRequest.projectId || "", reviewRequest.targetReviewDate)}
                          >
                            {reviewRequest.projectName}
                          </Link>
                        </TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getPurposeTypeNameByCode(reviewRequest.purpose || "") || ""}>{getPurposeTypeNameByCode(reviewRequest.purpose || "")}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getEquipmentUsageTypeNameByCode(reviewRequest.equipmentUsageType || "") || ""}>{getEquipmentUsageTypeNameByCode(reviewRequest.equipmentUsageType || "")}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getHeadquartersNameFromHierarchy(reviewRequest)}>{getHeadquartersNameFromHierarchy(reviewRequest)}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={getDivisionNameFromHierarchy(reviewRequest)}>{getDivisionNameFromHierarchy(reviewRequest)}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={reviewRequest.registrantName || ""}>{reviewRequest.registrantName}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        <TruncatedCell label={reviewRequest.targetReviewDate || ""}>{reviewRequest.targetReviewDate}</TruncatedCell>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        {(() => {
                          const phaseLabel = reviewRequest.reviewPhase
                            ? ReviewPhasePresets.find(p => p.value === reviewRequest.reviewPhase)?.label ?? reviewRequest.reviewPhase
                            : "";
                          return <TruncatedCell label={phaseLabel}>{phaseLabel}</TruncatedCell>;
                        })()}
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap">
                        <Flex gap={1} align="center" wrap="wrap" whiteSpace="nowrap">
                          {reviewRequest.requestType === "approval" ? (
                            <Tag colorScheme="red" size="sm" whiteSpace="nowrap" flexShrink={0}>{t('features.Home.requestType.approval')}</Tag>
                          ) : reviewRequest.requestType === "confirmation" ? (
                            <Tag colorScheme="orange" size="sm" whiteSpace="nowrap" flexShrink={0}>{t('features.Home.requestType.confirmation')}</Tag>
                          ) : (
                            <Tag colorScheme="blue" size="sm" whiteSpace="nowrap" flexShrink={0}>{t('features.Home.requestType.review')}</Tag>
                          )}
                          {reviewRequest.requestType === "review" && reviewRequest.daysOverdue === 0 && (
                            <Badge colorScheme="orange" fontSize="xs" px={2} py={0.5} borderRadius="md" whiteSpace="nowrap" flexShrink={0}>
                              {t('features.Home.statusBadge.today')}
                            </Badge>
                          )}
                          {reviewRequest.requestType === "review" && reviewRequest.daysOverdue > 0 && (
                            <Badge colorScheme="red" fontSize="xs" px={2} py={0.5} borderRadius="md" whiteSpace="nowrap" flexShrink={0}>
                              {t('features.Home.statusBadge.daysOverdue', { days: reviewRequest.daysOverdue })}
                            </Badge>
                          )}
                        </Flex>
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200">
                        {reviewRequest.requestType === "approval" ? (
                          reviewRequest.parallelApprovalStatus ? (
                            <ParallelApprovalIcons
                              approvalStatus={reviewRequest.approvalStatus || ""}
                              parallelApprovalStatus={reviewRequest.parallelApprovalStatus}
                              crossCheckRequired={reviewRequest.crossCheckRequired || false}
                              materialOfficerRequired={reviewRequest.materialOfficerRequired || false}
                            />
                          ) : (
                            <TruncatedCell label={reviewRequest.currentApproverInfo || ""}>{reviewRequest.currentApproverInfo}</TruncatedCell>
                          )
                        ) : null}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TabPanel>

            {/* ブックマークテーブル */}
            <TabPanel p={0} h="100%" overflow="auto">
              <Table border="1px solid" borderColor="gray.200" variant="simple" size="sm"
                sx={{ tableLayout: "fixed" }}
                width={bookmarkColWidths.length > 0 ? `${bookmarkColWidths.reduce((a, b) => a + b, 0)}px` : "100%"}
              >
                <Thead position="sticky" top={0} zIndex={1} sx={{ 'th input, th select': { ml: '-2px' } }}>
                  <Tr bgColor="gray.50">
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={bookmarkColWidths[0] ? `${bookmarkColWidths[0]}px` : "16%"} position="relative">
                      {t('features.Home.tableHeader.projectName')}{renderSortButton("projectName", sortBookmark, setSortBookmark)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterBookmarkProjectName} onChange={setFilterBookmarkProjectName} />
                      <ResizeHandle colIndex={0} setColWidths={setBookmarkColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={bookmarkColWidths[1] ? `${bookmarkColWidths[1]}px` : "10%"} position="relative">
                      {t('features.Home.tableHeader.reviewDate')}
                      <ResizeHandle colIndex={1} setColWidths={setBookmarkColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={bookmarkColWidths[2] ? `${bookmarkColWidths[2]}px` : "8%"} position="relative">
                      {t('features.Home.tableHeader.purpose')}{renderSortButton("purpose", sortBookmark, setSortBookmark)}
                      <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterBookmarkPurpose} onChange={(e) => setFilterBookmarkPurpose(e.target.value)}>
                        <option value="">{t('common.label.all')}</option>
                        {Object.values(PurposeType).map(({ code, name }) => <option key={code} value={name}>{getPurposeTypeNameByCode(code)}</option>)}
                      </Select>
                      <ResizeHandle colIndex={2} setColWidths={setBookmarkColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={bookmarkColWidths[3] ? `${bookmarkColWidths[3]}px` : "8%"} position="relative">
                      <Box as="span" whiteSpace="nowrap">{t('features.Home.tableHeader.equipmentUsageType')}{renderSortButton("equipmentUsageType", sortBookmark, setSortBookmark)}</Box>
                      <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterBookmarkEquipmentUsageType} onChange={(e) => setFilterBookmarkEquipmentUsageType(e.target.value)}>
                        <option value="">{t('common.label.all')}</option>
                        {Object.values(EquipmentUsageType).map(({ code }) => <option key={code} value={getEquipmentUsageTypeNameByCode(code)}>{getEquipmentUsageTypeNameByCode(code)}</option>)}
                      </Select>
                      <ResizeHandle colIndex={3} setColWidths={setBookmarkColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={bookmarkColWidths[4] ? `${bookmarkColWidths[4]}px` : "9%"} position="relative">
                      {t('features.Home.tableHeader.headquarters')}{renderSortButton("divisionHierarchy", sortBookmark, setSortBookmark)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterBookmarkHeadquarters} onChange={setFilterBookmarkHeadquarters} />
                      <ResizeHandle colIndex={4} setColWidths={setBookmarkColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={bookmarkColWidths[5] ? `${bookmarkColWidths[5]}px` : "9%"} position="relative">
                      {t('features.Home.tableHeader.division')}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterBookmarkDivision} onChange={setFilterBookmarkDivision} />
                      <ResizeHandle colIndex={5} setColWidths={setBookmarkColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={bookmarkColWidths[6] ? `${bookmarkColWidths[6]}px` : "10%"} position="relative">
                      {t('features.Home.tableHeader.customerName')}{renderSortButton("customerName", sortBookmark, setSortBookmark)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterBookmarkCustomerName} onChange={setFilterBookmarkCustomerName} />
                      <ResizeHandle colIndex={6} setColWidths={setBookmarkColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={bookmarkColWidths[7] ? `${bookmarkColWidths[7]}px` : "10%"} position="relative">
                      {t('features.Home.tableHeader.opportunityName')}{renderSortButton("opportunityName", sortBookmark, setSortBookmark)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterBookmarkOpportunityName} onChange={setFilterBookmarkOpportunityName} />
                      <ResizeHandle colIndex={7} setColWidths={setBookmarkColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={bookmarkColWidths[8] ? `${bookmarkColWidths[8]}px` : "8%"} position="relative">
                      {t('features.Home.tableHeader.registrant')}{renderSortButton("registrant", sortBookmark, setSortBookmark)}
                      <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterBookmarkRegistrant} onChange={setFilterBookmarkRegistrant} />
                      <ResizeHandle colIndex={8} setColWidths={setBookmarkColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={bookmarkColWidths[9] ? `${bookmarkColWidths[9]}px` : "8%"} position="relative">
                      {t('features.Home.tableHeader.applicationDate')}{renderSortButton("applicationDate", sortBookmark, setSortBookmark)}
                      <ResizeHandle colIndex={9} setColWidths={setBookmarkColWidths} />
                    </Th>
                    <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={bookmarkColWidths[10] ? `${bookmarkColWidths[10]}px` : "10%"} position="relative">
                      {t('features.Home.tableHeader.approveStatus')}{renderSortButton("approveStatus", sortBookmark, setSortBookmark)}
                      <ResizeHandle colIndex={10} setColWidths={setBookmarkColWidths} />
                    </Th>
                    <Th bgColor="gray.50" textAlign="center" width={bookmarkColWidths[11] ? `${bookmarkColWidths[11]}px` : "4%"} position="relative">
                      {t('features.Home.tableHeader.action')}
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredBookmarkProjects.map(bookmarkProject => {
                    const isLinkDisabled = !!bookmarkProject.deleted || !!bookmarkProject.accessDenied;
                    const statusLabel = bookmarkProject.accessDenied
                      ? t('features.Home.bookmark.accessDenied')
                      : bookmarkProject.deleted
                        ? t('features.Home.bookmark.deleted')
                        : (getStatusNameByCode(bookmarkProject.approvalStatus || "") || "");
                    return (
                      <Tr key={`${bookmarkProject.projectId}#${bookmarkProject.reviewDate || ""}`} _hover={{ bg: "gray.50" }}>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={bookmarkProject.projectName || ""}>
                            {isLinkDisabled ? (
                              <Text>{bookmarkProject.projectName}</Text>
                            ) : (
                              <Link
                                as={RouterLink}
                                to={ApplicationPathName.InvestmentScreeningDetail.Url}
                                state={{ projectId: bookmarkProject.projectId, reviewDate: bookmarkProject.reviewDate || undefined }}
                                textColor={'blue.500'}
                                textDecoration={'underline'}
                                onClick={(e) => openInvestmentScreeningDetailInNewTab(e, bookmarkProject.projectId || "", bookmarkProject.reviewDate)}
                                onAuxClick={(e) => openInvestmentScreeningDetailInNewTab(e, bookmarkProject.projectId || "", bookmarkProject.reviewDate)}
                              >
                                {bookmarkProject.projectName}
                              </Link>
                            )}
                          </TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={bookmarkProject.reviewDate || ""}>{bookmarkProject.reviewDate}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={getPurposeTypeNameByCode(bookmarkProject.purpose || "") || ""}>{getPurposeTypeNameByCode(bookmarkProject.purpose || "")}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={getEquipmentUsageTypeNameByCode(bookmarkProject.equipmentUsageType || "") || ""}>{getEquipmentUsageTypeNameByCode(bookmarkProject.equipmentUsageType || "")}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={getHeadquartersNameFromHierarchy(bookmarkProject)}>{getHeadquartersNameFromHierarchy(bookmarkProject)}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={getDivisionNameFromHierarchy(bookmarkProject)}>{getDivisionNameFromHierarchy(bookmarkProject)}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={bookmarkProject.customerName || ""}>{bookmarkProject.customerName}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={bookmarkProject.opportunityName || ""}>{bookmarkProject.opportunityName}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={bookmarkProject.registrantName || ""}>{bookmarkProject.registrantName}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={bookmarkProject.applicationDay || ""}>{bookmarkProject.applicationDay}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={statusLabel}>{statusLabel}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          <Tooltip label={t('features.Home.ariaLabel.removeBookmark')}>
                            <IconButton
                              aria-label={t('features.Home.ariaLabel.removeBookmark')}
                              icon={<Icon as={MdDeleteOutline} boxSize={5} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => {
                                setBookmarkToRemove(bookmarkProject);
                                onOpenBookmarkRemoveConfirm();
                              }}
                            />
                          </Tooltip>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* ブックマーク削除確認モーダル */}
      <ConfirmModal
        isOpen={isOpenBookmarkRemoveConfirm}
        title={t('features.Home.bookmark.removeConfirmTitle')}
        message={t('features.Home.bookmark.removeConfirmMessage')}
        onOk={async () => {
          if (bookmarkToRemove) {
            await removeBookmark(bookmarkToRemove.projectId, bookmarkToRemove.reviewDate || null);
            await fetchBookmarkProjects();
          }
          setBookmarkToRemove(null);
          onCloseBookmarkRemoveConfirm();
        }}
        onCancel={() => {
          setBookmarkToRemove(null);
          onCloseBookmarkRemoveConfirm();
        }}
      />

      {/* お知らせドロワー */}
      <Drawer isOpen={isOpenDrawer} placement="right" onClose={onCloseDrawer} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Flex align="center" gap={2}>
              <Icon as={MdNotifications} boxSize={5} color="blue.500" />
              <Text>{t('features.Home.notification.title')}</Text>
            </Flex>
          </DrawerHeader>
          <DrawerBody p={0}>
            {notifications.map(notification => (
              <Box
                key={notification.notificationId}
                p={4}
                borderBottom="1px solid"
                borderColor="gray.100"
                _hover={{ bg: "gray.50" }}
                cursor="pointer"
                onClick={() => {
                  setSelectedNotification(notification);
                  onOpenNotificationModal();
                }}
              >
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="xs" color="gray.500">{notification.publishDate}</Text>
                  {isNewNotification(notification) && (
                    <Badge colorScheme="red" fontSize="xs">NEW</Badge>
                  )}
                </Flex>
                <Text fontSize="sm" noOfLines={2}>{getMultilingualDisplayName(notification.title)}</Text>
              </Box>
            ))}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* お知らせ詳細モーダル */}
      <Modal isOpen={isOpenNotificationModal} onClose={handleCloseNotificationModal} isCentered scrollBehavior="inside" size="2xl" motionPreset="slideInBottom">
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="16px" overflow="hidden" shadow="2xl">
          <Box bgGradient="linear(to-r, blue.500, blue.600)" px={6} py={4}>
            <Flex align="center" gap={2} color="white">
              <Icon as={MdNotifications} boxSize={6} />
              <Text fontSize="lg" fontWeight="bold" noOfLines={2}>{getMultilingualDisplayName(selectedNotification?.title)}</Text>
            </Flex>
            <Flex align="center" gap={1} mt={2} color="whiteAlpha.800">
              <Icon as={MdCalendarToday} boxSize={3.5} />
              <Text fontSize="xs">{selectedNotification?.publishDate}</Text>
            </Flex>
          </Box>
          <ModalCloseButton color="white" />
          <ModalBody py={5} px={6}>
            <Box
              whiteSpace="pre-wrap"
              fontSize="sm"
              lineHeight="1.9"
              p={5}
              bg="gray.50"
              borderRadius="10px"
              borderLeft="4px solid"
              borderLeftColor="blue.400"
              minH="120px"
            >
              {getMultilingualDisplayName(selectedNotification?.body)}
            </Box>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100" py={3}>
            <Button colorScheme="blue" borderRadius="8px" onClick={handleCloseNotificationModal}>{t('common.button.close')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Home;
